// The small Markdown subset Modeling Process feedback is stored in: **bold**, *italic*, ++underline++
// (not standard Markdown, markdown-it-ins' syntax), "- " bullets and "1. " numbered items, one per line,
// without nesting. Users never see it: RichTextEditor turns what they type into it and markdownToHtml
// shows it back. Messages written before it existed are plain text and read the same, since a lone
// marker without its pair stays as typed. The parser is ported to functions/feedbackMarkdown.js for
// Slack, keep both in sync.

const MARKERS = [
  { token: '**', style: 'bold' },
  { token: '++', style: 'underline' },
  { token: '*', style: 'italic' }
]
const STYLES = ['bold', 'italic', 'underline']
const ESCAPABLE = '\\*+-.'
const BULLET = /^- (.*)$/
const NUMBERED = /^(\d+)\. (.*)$/

function sameStyle (a, b) {
  return STYLES.every(style => a[style] === b[style])
}

// Adjacent pieces that look the same become one, so their markers aren't closed and reopened
function mergeSegments (segments) {
  let merged = []
  segments.forEach(segment => {
    const last = merged[merged.length - 1]
    if (last && sameStyle(last, segment)) {
      last.text += segment.text
    } else if (segment.text) {
      merged.push(Object.assign({}, segment))
    }
  })
  return merged
}

// Line ends count as spaces
function isSpace (char) {
  return char === undefined || /\s/.test(char)
}

// A line's text split by style: [{ text, bold, italic, underline }]. Markers toggle their style, so
// the order of adjacent ones doesn't matter; a marker left without its pair is plain text
export function parseInline (line) {
  let tokens = []
  let text = ''
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '\\' && i + 1 < line.length && ESCAPABLE.indexOf(line[i + 1]) >= 0) {
      text += line[i + 1]
      i++
      continue
    }
    const marker = MARKERS.find(item => line.substr(i, item.token.length) === item.token)
    // One with spaces on both sides isn't formatting, e.g. "5 * 3" in a message written before it
    if (marker && isSpace(line[i - 1]) && isSpace(line[i + marker.token.length])) {
      text += marker.token
      i += marker.token.length - 1
    } else if (marker) {
      if (text) {
        tokens.push({ text: text })
        text = ''
      }
      tokens.push({ marker: marker })
      i += marker.token.length - 1
    } else {
      text += char
    }
  }
  if (text) {
    tokens.push({ text: text })
  }
  MARKERS.forEach(marker => {
    const indexes = tokens.map((token, index) => token.marker === marker ? index : -1).filter(index => index >= 0)
    if (indexes.length % 2 === 1) {
      tokens[indexes[indexes.length - 1]] = { text: marker.token }
    }
  })
  let state = { bold: false, italic: false, underline: false }
  let segments = []
  tokens.forEach(token => {
    if (token.marker) {
      state[token.marker.style] = !state[token.marker.style]
    } else {
      segments.push(Object.assign({ text: token.text }, state))
    }
  })
  return mergeSegments(segments)
}

// One entry per line: { type: 'text' | 'bullet' | 'numbered', number, segments }
export function parseFeedback (markdown) {
  return (markdown || '').split(/\r?\n/).map(line => {
    const bullet = BULLET.exec(line)
    if (bullet) {
      return { type: 'bullet', segments: parseInline(bullet[1]) }
    }
    const numbered = NUMBERED.exec(line)
    if (numbered) {
      return { type: 'numbered', number: parseInt(numbered[1], 10), segments: parseInline(numbered[2]) }
    }
    return { type: 'text', segments: parseInline(line) }
  })
}

function escapeHtml (text) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function segmentsToHtml (segments) {
  return segments.map(segment => {
    let html = escapeHtml(segment.text)
    if (segment.underline) {
      html = `<u>${html}</u>`
    }
    if (segment.italic) {
      html = `<em>${html}</em>`
    }
    if (segment.bold) {
      html = `<strong>${html}</strong>`
    }
    return html
  }).join('')
}

// Safe for v-html: all text is escaped and only these tags are produced. Each text line is a div, as
// contenteditable writes them, so the same HTML also loads a message into RichTextEditor
export function markdownToHtml (markdown) {
  let html = ''
  let list = null
  parseFeedback(markdown).forEach(line => {
    const listTag = line.type === 'bullet' ? 'ul' : (line.type === 'numbered' ? 'ol' : null)
    if (list && list !== listTag) {
      html += `</${list}>`
      list = null
    }
    if (listTag && !list) {
      // Keeps the numbering of lists that don't start at 1, e.g. plain text messages that continue one
      html += listTag === 'ol' && line.number !== 1 ? `<ol start="${line.number}">` : `<${listTag}>`
      list = listTag
    }
    const content = segmentsToHtml(line.segments) || '<br>'
    html += listTag ? `<li>${content}</li>` : `<div>${content}</div>`
  })
  if (list) {
    html += `</${list}>`
  }
  return html
}

const BLOCK_TAGS = ['DIV', 'P', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE']
const SKIPPED_TAGS = ['SCRIPT', 'STYLE', 'TEMPLATE']

// Styles an element adds, whether the browser wrote it as a tag or as inline CSS
function elementStyle (element, style) {
  const tag = element.tagName
  const css = element.style || {}
  const weight = css.fontWeight
  return {
    bold: style.bold || tag === 'B' || tag === 'STRONG' || weight === 'bold' || weight === 'bolder' || parseInt(weight, 10) >= 600,
    italic: style.italic || tag === 'I' || tag === 'EM' || css.fontStyle === 'italic',
    underline: style.underline || tag === 'U' || (css.textDecoration || '').indexOf('underline') >= 0 || (css.textDecorationLine || '').indexOf('underline') >= 0
  }
}

function escapeMarkdown (text) {
  return text.replace(/[\\*+]/g, '\\$&')
}

// Spaces at the ends of a styled piece go outside its markers, so that markers always touch text
function unstyleEndSpaces (segments) {
  let result = []
  segments.forEach(segment => {
    const match = /^(\s*)([\s\S]*?)(\s*)$/.exec(segment.text)
    const plain = { bold: false, italic: false, underline: false }
    result.push(Object.assign({ text: match[1] }, plain))
    result.push(Object.assign({}, segment, { text: match[2] }))
    result.push(Object.assign({ text: match[3] }, plain))
  })
  return mergeSegments(result)
}

function segmentsToMarkdown (segments) {
  let markdown = ''
  let state = { bold: false, italic: false, underline: false }
  const toggle = style => {
    markdown += MARKERS.find(marker => marker.style === style).token
    state[style] = !state[style]
  }
  segments.forEach(segment => {
    STYLES.filter(style => state[style] !== segment[style]).forEach(toggle)
    markdown += escapeMarkdown(segment.text)
  })
  STYLES.filter(style => state[style]).forEach(toggle)
  return markdown
}

// The editor's content as stored: its lines, lists and styles; anything else, e.g. pasted formatting,
// is dropped. Nested lists become items of the list they're in
export function htmlToMarkdown (root) {
  let lines = []
  let current = null
  const closeLine = () => {
    current = null
  }
  const openLine = (type, number) => {
    current = { type: type, number: number, segments: [] }
    lines.push(current)
  }
  const addText = (text, style) => {
    text.replace(/\r/g, '').replace(/\u00a0/g, ' ').split('\n').forEach((part, index) => {
      if (index > 0) {
        closeLine()
      }
      if (!current) {
        openLine('text')
      }
      current.segments.push(Object.assign({ text: part }, style))
    })
  }
  const walk = (node, style, list) => {
    if (node.nodeType === Node.TEXT_NODE) {
      addText(node.nodeValue, style)
      return
    }
    if (node.nodeType !== Node.ELEMENT_NODE || SKIPPED_TAGS.indexOf(node.tagName) >= 0) {
      return
    }
    const tag = node.tagName
    if (tag === 'BR') {
      // A <br> ends its line, and on its own it is an empty line
      if (!current) {
        openLine('text')
      }
      closeLine()
      return
    }
    let childList = list
    if (tag === 'UL') {
      childList = { type: 'bullet' }
    } else if (tag === 'OL') {
      const start = parseInt(node.getAttribute('start'), 10)
      childList = { type: 'numbered', next: isNaN(start) ? 1 : start }
    }
    const block = BLOCK_TAGS.indexOf(tag) >= 0 || childList !== list
    if (block) {
      closeLine()
    }
    if (tag === 'LI') {
      const itemList = list || { type: 'bullet' }
      openLine(itemList.type, itemList.type === 'numbered' ? itemList.next++ : null)
    }
    const childStyle = elementStyle(node, style)
    Array.from(node.childNodes).forEach(child => walk(child, childStyle, childList))
    if (block) {
      closeLine()
    }
  }
  Array.from(root.childNodes).forEach(child => walk(child, { bold: false, italic: false, underline: false }, null))

  let markdown = []
  lines.forEach(line => {
    const content = segmentsToMarkdown(unstyleEndSpaces(line.segments))
    if (line.type === 'bullet' || line.type === 'numbered') {
      // An empty item is one the user left behind, e.g. by pressing Enter at the end of a list
      if (content.trim()) {
        markdown.push(line.type === 'bullet' ? `- ${content}` : `${line.number}. ${content}`)
      }
    } else {
      // Text that would read as a list item stays text
      markdown.push(content.replace(/^-( )/, '\\-$1').replace(/^(\d+)\.( )/, '$1\\.$2'))
    }
  })
  while (markdown.length > 0 && !markdown[markdown.length - 1].trim()) {
    markdown.pop()
  }
  return markdown.join('\n')
}
